import React, { useState } from "react"
import { useQueries } from "react-query"
import {
  Box, Flex, Radio, RadioGroup, Spinner, Stack, Text,
} from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import Avatar from "components/elements/Avatar"
import { asDate } from "utilities/strings"
import useCurrentAccount from "services/useCurrentAccount"
import { useEvents } from "queries/events"
import { languageTemplatesCacheKey } from "queries/languageTemplates"
import { getLanguageTemplates } from "api/languageTemplates"
import { DioboxEvent, LanguageTemplate } from "sharedTypes"

type Props = {
  eventId: string
  onClose: () => void
  onCopy: (sourceEventId: string, sourceLanguageTemplateId: string) => Promise<void>
}

type CopyableTemplate = {
  template: LanguageTemplate
  sourceEvent: DioboxEvent
}

const CopyTemplateModal = ({ eventId, onClose, onCopy }: Props) => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const currentAccount = useCurrentAccount()
  // Stopgap: bounded to upcoming events (same "time" filter EventsIndex uses) rather than the
  // account's entire event history, so this doesn't fire a GET per other event, unbounded, every
  // time the modal opens. Still a per-event fan-out under the hood — a proper account-wide
  // "all my templates" endpoint is the real fix, tracked separately.
  const { data: events = [], isLoading: isLoadingEvents } = useEvents(currentAccount.id, "future")
  const otherEvents = events.filter((event) => event.id !== eventId)

  // One useQueries call (not a per-event useQuery in a loop) so a variable-length event list
  // doesn't run afoul of the rules of hooks — see queries/languageTemplates.ts for the same key.
  // staleTime keeps a re-open of the modal within a few minutes from re-firing the whole
  // fan-out if nothing's changed.
  const templateQueries = useQueries(otherEvents.map((event) => ({
    queryKey: languageTemplatesCacheKey(event.id),
    queryFn: () => getLanguageTemplates(event.id).then(({ data }) => data.data),
    staleTime: 5 * 60 * 1000,
  })))
  const isLoading = isLoadingEvents || templateQueries.some((query) => query.isLoading)

  // Shared/offered templates (English/French/Spanish) already show up for every event —
  // copying one would be redundant, so only each event's own custom templates are listed.
  const copyableTemplates: CopyableTemplate[] = otherEvents.flatMap((event, index) => {
    const templates = (templateQueries[index]?.data ?? []) as LanguageTemplate[]

    return templates
      .filter((template) => !template.isOffered)
      .map((template) => ({ template, sourceEvent: event }))
  })

  const selected = copyableTemplates.find(
    (entry) => `${entry.sourceEvent.id}:${entry.template.id}` === selectedKey,
  )

  const handleCopy = async () => {
    if (selected) {
      await onCopy(selected.sourceEvent.id, selected.template.id)
      onClose()
    }
  }

  return (
    <Modal
      show
      hide={onClose}
      title="Copy Language Template"
      size="2xl"
      actions={[
        <CancelButton onClick={onClose} />,
        <Button isDisabled={!selected} onClick={handleCopy}>Copy Template</Button>,
      ]}
    >
      <Text mb={6}>
        Select a template from another event to copy to this event. The copied
        template can be edited independently without affecting the original.
      </Text>
      {isLoading ? (
        <Flex justify="center" py={8}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      ) : copyableTemplates.length === 0 ? (
        <Box>
          <Text fontWeight="semibold" color="red.400" mb={1}>No templates available</Text>
          <Text color="gray.600">
            No other events are available to copy from.
          </Text>
        </Box>
      ) : (
        <RadioGroup value={selectedKey ?? ""} onChange={setSelectedKey} w="100%">
          <Stack spacing={4}>
            {copyableTemplates.map(({ template, sourceEvent }) => (
              <Radio
                key={`${sourceEvent.id}:${template.id}`}
                value={`${sourceEvent.id}:${template.id}`}
                w="100%"
              >
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  alignItems="center"
                  w="100%"
                  ml={2}
                >
                  <Text>{template.name}</Text>
                  <Flex align="center" gap={2}>
                    <Avatar
                      size="sm"
                      borderRadius="md"
                      name={sourceEvent.title}
                      src={sourceEvent.website?.coverImageUrl}
                    />
                    <Box>
                      <Text fontSize="sm" noOfLines={1}>{sourceEvent.title}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {asDate(sourceEvent.startDate, "PP")}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      )}
    </Modal>
  )
}

export default CopyTemplateModal
